import { Note, NoteRelevance } from '@shared/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Eye, PencilIcon, TrashIcon, FileText, AlertCircle, Star, Paperclip } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const relevanceOptions: { value: NoteRelevance; label: string; icon: typeof FileText }[] = [
  { value: 'normal', label: 'Normal', icon: FileText },
  { value: 'importante', label: 'Importante', icon: AlertCircle },
  { value: 'destacada', label: 'Destacada', icon: Star },
];

const getRelevanceBadgeVariant = (relevance: NoteRelevance) => {
  switch (relevance) {
    case 'destacada': return 'destructive';
    case 'importante': return 'warning';
    default: return 'secondary';
  }
};

const getRelevanceIcon = (relevance: NoteRelevance) => {
  const option = relevanceOptions.find(o => o.value === relevance);
  return option?.icon || FileText;
};

const truncateContent = (content: string, maxLines: number = 2) => {
  const lines = content.split('\n').slice(0, maxLines);
  const truncated = lines.join('\n');
  return truncated.length < content.length ? truncated + '...' : truncated;
};

interface NoteCardProps {
  note: Note;
  onView: (note: Note) => void;
  onEdit: (note: Note) => void;
  onDelete: (id: string) => void;
}

export function NoteCard({ note, onView, onEdit, onDelete }: NoteCardProps) {
  const RelevanceIcon = getRelevanceIcon(note.relevance);
  const hasAttachments = note.adjuntos && note.adjuntos.length > 0;

  return (
    <Card
      className={cn(
        "cursor-pointer hover:shadow-md transition-shadow",
        note.relevance === 'destacada' && "border-destructive/50",
        note.relevance === 'importante' && "border-yellow-500/50"
      )}
      onClick={() => onView(note)}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg line-clamp-1">{note.title}</CardTitle>
          <div className="flex items-center gap-1">
            {hasAttachments && (
              <Badge variant="outline" className="gap-1">
                <Paperclip className="h-3 w-3" />
                {note.adjuntos!.length}
              </Badge>
            )}
            <Badge variant={getRelevanceBadgeVariant(note.relevance)} className="gap-1 capitalize shrink-0">
              <RelevanceIcon className="h-3 w-3" />
              {note.relevance}
            </Badge>
          </div>
        </div>
        <CardDescription>
          {format(new Date(note.createdAt), 'PPP', { locale: es })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-wrap">
          {truncateContent(note.content, 3)}
        </p>
        <div className="flex justify-end gap-1 mt-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onView(note); }}
            title="Ver nota"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onEdit(note); }}
            title="Editar nota"
          >
            <PencilIcon className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onDelete(note.id); }}
            title="Eliminar nota"
          >
            <TrashIcon className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
